import { useTour } from '@reactour/tour';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Datepicker, { DatepickerType, DateValueType } from 'react-tailwindcss-datepicker';
import { useTheme } from 'styled-components';

import { ExportDocIcon } from '@/components/common/export-doc-icon';
import { Modal, useModal } from '@/components/common/modal';
import { PlusIcon } from '@/components/common/plus-icon';
import { SpinningIcon } from '@/components/common/spinning-icon';
import { Switch } from '@/components/common/switch';
import { Table } from '@/components/common/table';
import { TableState } from '@/components/common/table/types';
import { TopBar } from '@/components/common/top-bar';
import { Button, ButtonVariant } from '@/components/form/button';
import { SearchInput } from '@/components/form/search-input';
import { useAuth } from '@/features/auth';
import { getTourStep } from '@/features/demo/steps';
import { useExport } from '@/hooks/use-export';
import { useRoute } from '@/hooks/use-route';
import { MeasurementOverviewLayout } from '@/layouts';
import { getAdminGroup } from '@/utils/get-admin-group';
import { Theme } from '@/utils/types';

import { AveragePopup } from '../components/average-popup';
import { CreateAnalysisWizard } from '../components/create-analysis-wizard';
import { getBatches } from '../services/batches';
import { SingleMeasurmentQueryKeys } from '../services/query-keys';
import { IBatch } from '../types/batch';
import { BioEthMethods, WineMethods, YeastMethods } from '../types/method';
import { State } from '../types/state';
import { measurementColumns } from '../utils/measurement-columns';

const calendarPrimaryColorMatrix = {
  [Theme.Beer]: 'emerald',
  [Theme.Wine]: 'rose',
  [Theme.Ethanol]: 'sky',
};

const ANALYSIS_HISTORY_DATES_KEY = 'analysis-history-dates';

export const AnalysisHistoryPage = () => {
  const { t } = useTranslation();
  const theme: any = useTheme();
  const { isOpen, toggle } = useModal();
  const { routeToHome, routeToAnalysis } = useRoute();
  const calendarPrimaryColor = useMemo(
    () => calendarPrimaryColorMatrix[theme.current as Theme],
    [theme.current],
  );
  const [data, setData] = useState<IBatch[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalAnalysis, setTotalAnalysis] = useState(0);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [queryState, setQueryState] = useState<TableState>('none');
  const [dates, setDates] = useState<DateValueType>(() => {
    try {
      const stored = localStorage.getItem(ANALYSIS_HISTORY_DATES_KEY);
      return stored ? JSON.parse(stored) : { startDate: null, endDate: null };
    } catch {
      return { startDate: null, endDate: null };
    }
  });
  const [showFermentation, setShowFermentation] = useState<boolean>(false);
  const [selectedBatches, setSelectedBatches] = useState<IBatch[]>([]);
  const { generateExportZipBundle, isExporting } = useExport();

  // demo
  const {
    isOpen: isTourOpen,
    setIsOpen: setIsTourOpen,
    setCurrentStep,
    steps,
  } = useTour();
  const [wasTourOpen, setWasTourOpen] = useState<Boolean>(false);

  // a counter to reset row selection in table
  const [resetRowSelection, setResetRowSelection] = useState<number>(0);

  // determine analysis method based on signed in method
  const { user } = useAuth();

  // a trick to wait until the data is loaded
  useEffect(() => {
    setWasTourOpen(isTourOpen); // remember the tour state
    setIsTourOpen(false); // temp turn off the tour, once data is loaded then we can set it open
  }, []);

  useEffect(() => {
    // remove previous toasts immediately
    toast.remove();

    const batchCount = selectedBatches.length;
    if (batchCount <= 0) return;

    const averageConz =
      selectedBatches.reduce((accum, batch) => accum + batch.resultStat.conz || 0, 0) /
      batchCount;
    const totalViab =
      selectedBatches.reduce((accum, batch) => accum + batch.resultStat?.viab || 0, 0) /
      batchCount;

    toast.custom(
      (t) => (
        <AveragePopup
          t={t}
          conz={averageConz}
          viab={totalViab}
          onClickExport={exportSelectedRowsAsZip}
          onClose={() => setResetRowSelection(resetRowSelection + 1)}
        />
      ),
      {
        duration: Infinity,
        position: 'bottom-center',
      },
    );
  }, [selectedBatches]);

  const analysisMethod = useMemo(() => {
    if (user?.signedInMethod === 'wine') return WineMethods;
    if (user?.signedInMethod === 'beer') return YeastMethods;
    if (user?.signedInMethod === 'ethanol') return BioEthMethods;
    return [];
  }, [user]);

  // get admin role
  const group = useMemo(() => getAdminGroup(user?.roles), [user]);

  // query data
  // right now we only fetch 'result ready' single measurements
  const states = [State.ResultReady, State.WineResultReady, State.UploadCompleted];
  const { isFetching } = useQuery({
    queryKey: [
      SingleMeasurmentQueryKeys.GetAllBatches,
      nameFilter,
      dates,
      page,
      showFermentation,
    ],
    queryFn: () =>
      getBatches({
        page,
        pageSize: 50,
        states,
        methods: analysisMethod,
        name: nameFilter,
        group,
        startDate: dates?.startDate,
        endDate: dates?.endDate,
        pagination: true,
        includeFermentations: showFermentation,
      }),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    onSuccess: (newData) => {
      setData([...data, ...newData.data]);
      setQueryState(newData.data.length === 0 ? 'no-data' : 'none');
      setTotalPages(newData.pagination.total_pages);
      setTotalAnalysis(newData.pagination.total);

      // if tour was open, then we open the tour again
      if (wasTourOpen) {
        setIsTourOpen(true);
        setCurrentStep(getTourStep(steps, 'GxV3pF1'));
      }
    },
  });

  const resetStates = () => {
    setData([]);
    setPage(1);
    setQueryState('loading');
  };

  // apply search filter
  const onSearch = (search: string) => {
    setNameFilter(search);
    resetStates();
  };

  // apply show fermentation filter
  const onFilterFermentation = (state: boolean) => {
    setShowFermentation(state);
    resetStates();
  };

  // apply date filter
  const handleOneDateChange = (newDates: DateValueType) => {
    if (!newDates) return;
    setDates(newDates);
    localStorage.setItem(ANALYSIS_HISTORY_DATES_KEY, JSON.stringify(newDates));
    resetStates();
  };

  const handleOnRowClick = (data: IBatch) => {
    routeToAnalysis(data.id);
    return;
  };

  const fetchNextPage = () => {
    if (page <= totalPages) setPage(page + 1);
  };

  const exportSelectedRowsAsZip = () => {
    if (selectedBatches.length <= 0) {
      toast.error('Please choose at least one analysis to export.', {
        duration: 4000,
        position: 'top-right',
      });
      return;
    }
    if (selectedBatches.length > 0) generateExportZipBundle(selectedBatches);
  };

  const handleRowSelect = (batches: IBatch[]) => {
    // add selected batches, so that it could be used in export
    setSelectedBatches(batches);
  };

  return (
    <>
      <Modal title="" isOpen={isExporting} toggle={() => {}} hideCloseBtn>
        <div className="flex items-center gap-4">
          <SpinningIcon color={`fill-${theme.current}-primary`} />
          <p className="text-lg text-gray-800">
            {t('Please wait! Your CSV file is getting ready for download.')}
          </p>
        </div>
      </Modal>
      <CreateAnalysisWizard isOpen={isOpen} close={toggle} />
      <MeasurementOverviewLayout>
        <TopBar onBack={routeToHome}>
          <div className="flex gap-4">
            <Button
              variant={ButtonVariant.Link}
              classnames="flex items-center gap-2"
              isLoading={isExporting}
              onClick={exportSelectedRowsAsZip}
            >
              <ExportDocIcon />
              <span className={`text-sm font-semibold text-${theme.current}-primary`}>
                {t('CSV Export')}
              </span>
            </Button>
            <Button
              variant={ButtonVariant.Link}
              classnames="flex items-center gap-2"
              onClick={toggle}
            >
              <PlusIcon />
              <span className={`text-sm font-semibold text-${theme.current}-primary`}>
                {t('common.new')}
              </span>
            </Button>
          </div>
        </TopBar>

        <MeasurementOverviewLayout.Title title="measurements.title">
          <div className="grid grid-cols-2 gap-2">
            <Datepicker
              primaryColor={calendarPrimaryColor as DatepickerType['primaryColor']}
              startWeekOn="mon"
              value={dates}
              containerClassName={'w-full relative'}
              inputClassName={
                'relative w-full pl-4 pr-14 tracking-wide focus:ring-0 focus:border-gray-500 rounded-md bg-gray border-gray-100'
              }
              onChange={handleOneDateChange}
            />
            <SearchInput onSearch={onSearch} isLoading={isFetching} />
          </div>
        </MeasurementOverviewLayout.Title>

        {/*  search and fermentation show toggle section */}
        <div className="mt-10">
          <div className="flex items-center justify-between font-light text-gray-600 text-md">
            <div>
              {t('measurements.analysisCount', {
                total: queryState === 'loading' || isFetching ? '...' : totalAnalysis,
              })}{' '}
              {nameFilter ? (
                <>
                  {t('common.searchFor')}{' '}
                  <span className="font-semibold">{nameFilter}</span>
                </>
              ) : null}
            </div>
            <div className="flex items-center gap-3">
              <span>{t('measurements.showFermentationMeasurements')}</span>
              <div>
                <Switch isToggled={showFermentation} onToggle={onFilterFermentation} />
              </div>
            </div>
          </div>
        </div>

        <MeasurementOverviewLayout.Body>
          <Table<IBatch>
            data={data}
            columns={measurementColumns}
            fetchNextPage={fetchNextPage}
            state={isFetching ? 'loading' : queryState}
            hiddenColumnIds={group ? ['method'] : ['email', 'method']} // hide nothing if admin, otherwise hide email
            onRowClick={handleOnRowClick}
            onRowSelect={handleRowSelect}
            resetRowSelect={resetRowSelection}
            estimatedRows={totalAnalysis}
          />
        </MeasurementOverviewLayout.Body>
      </MeasurementOverviewLayout>
    </>
  );
};
